import React, { useContext, useEffect, useState } from 'react';
import appContext from '../AppContext';
import { getNearSchools, getAllSchools } from '../services/ESService';
import { getBoundaries, getBoundaryPolygons } from '../services/BoundaryService';
import { getAddress, dcAddressToTitleCase } from '../services/MARService';
import {filterSortSchools} from '../services/WranglingService';
import { useTranslation } from "react-i18next";

function MARInput() {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionArr, setSuggestionArr] = useState([]);
    const [value, setValue] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState("");

    const { filterInit, setOriginSchools, setSchools, setInBoundaryFilter, gradeFilters, wardFilters, offeringSorts, setLoadingData, setSearchedPlace, inBoundaryFilter, setBoundaryPolys} = useContext(appContext);
    const { t } = useTranslation();

    // set up properties and state for component
    let suggestionlist;

    useEffect(() => {
        // if address in URL params - create school list here based on found address
        if (window.location.search !== "" && filterInit) {
            const params = new URL(window.location).searchParams;
            const paramAddress = params.get('addr');
            if (paramAddress) {
                setLoadingData(true);
                setSelectedAddress(dcAddressToTitleCase(paramAddress));
                search(paramAddress);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterInit]);

    // watch to see if the list changes and needs to be replaced
    useEffect(() => {
        if (suggestionlist !== undefined && suggestionlist !== []) {
            const sortedArr = suggestionlist.slice().sort();
            let results = [];
            for (let i = 0; i < sortedArr.length - 1; i++) {
                if (sortedArr[i + 1] === sortedArr[i]) {
                    results.push(sortedArr[i]);
                }
            }
            if (results.length > 0) {
                throw new Error(`suggestion list contains duplicate values: ${results.toLocaleString()}`);
            }
        }
    }, [suggestionlist]);

    // watch to see if value changes
    useEffect(() => {
        if (value.length > 0 && value !== selectedAddress) {
            findMatch(value.replace(',', '').replace('.', ''));
        } else {
            setSuggestionArr([]);
            setSelectedIndex(undefined);
            setShowSuggestions(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    function clearText() {
        // clears the text from the default example address
        setValue('');
        setLoadingData(true);
        getAllSchools().then( data => {
            setSearchedPlace(null);
            setSuggestionArr([]);
            setInBoundaryFilter(false);
            setSelectedIndex(undefined);
            setOriginSchools([...data]);

            // set default schools if no address
            setSchools(filterSortSchools(data, gradeFilters, wardFilters, offeringSorts, false));
        }).catch( error => {
            console.error("Error in searching all schools", error);
        }).finally(() => {
            setLoadingData(false);
        });
    }

    function handleChange(e) {
        // on change of input value, search location service to find matching addresses for autocomplete
        setValue(e.target.value);
    }

    function onKeyDown(e) {
        // handle up and down for autocomplete
        switch (e.key) {
        case 'ArrowUp':
            if (suggestionArr.length > 0) {
                setSelectedIndex((selectedIndex === undefined || selectedIndex === 0) ? suggestionArr.length - 1 : selectedIndex - 1);
            }
            break;
        case 'ArrowDown':
            if (suggestionArr.length > 0) {
                setSelectedIndex((selectedIndex === undefined || selectedIndex === suggestionArr.length - 1) ?
                    0 : selectedIndex + 1);
            }
            break;
        case 'Enter':
            // handle on key enter - if selected value in auto complete, get that one, otherwise use top result
            if (e.key === 'Enter') {
                e.preventDefault();
                // handle alternative clearing of text
                if (value === "") {
                    clearText();
                } else {
                    if (selectedIndex !== undefined) {
                        onSelect(suggestionArr[selectedIndex]);
                    } else if (suggestionArr.length > 0) {
                        onSelect(suggestionArr[0]);
                    } else {
                        search(value);
                    }
                }
            }
            break;
        default:
            break;
        }
    }

    function search(chkValue) {
        // if item choosen in either autocomplete or through enter, run the search
        setLoadingData(true);
        if (chkValue === "") {
            clearText();
        } else {
            getAddress(chkValue, true).then(response => {
                if (response && (chkValue.toLowerCase() === response.address.properties.FullAddress.toLowerCase() || chkValue.toLowerCase() === response.address.properties.Alias.toLowerCase())) {
                    setValue(dcAddressToTitleCase(chkValue));
                    setSearchedPlace(response.address);
                    setSuggestionArr([]);
                    setSelectedIndex(undefined);

                    // get schools nearest and get boundaries
                    let llLocationObj = {lat: response.address.properties.Latitude, lon: response.address.properties.Longitude};
                    let getSchools = getNearSchools(llLocationObj);
                    // let getBoundary = getBoundaries({x: response.address.properties.Xcoord, y: response.address.properties.Ycoord});
                    let getNewSchools = getBoundaries({x: response.address.properties.Xcoord, y: response.address.properties.Ycoord});

                    // Promise.allSettled([getSchools, getBoundary]).then((data) => {
                    Promise.allSettled([getSchools, getNewSchools]).then((data) => {

                        // console.log('getNewSchools status: ', data[1].status);
                        // console.log('getBoundary:  ', data[1].value['newBoundarySchools']);
                        if (data[0].status === 'fulfilled' && data[1].status === 'rejected') {
                            setOriginSchools([...(data[0].value)]);
                            // console.log('set from mar input rejected');
                            setSchools(filterSortSchools(data[0].value, gradeFilters, wardFilters, offeringSorts, inBoundaryFilter));
                        } else if (data[0].status === 'fulfilled' && data[1].status === 'fulfilled') {                       
                            // match up schools and set property inBoundary
                            let matchedSchools = matchInBoundary(data[0].value, data[1].value['newBoundarySchools']);
                            setOriginSchools([...matchedSchools]);

                            // match up schools and set property proximity (optional schools)
                            let optionalSchools = matchOptional(data[0].value, data[1].value['optionalSchools']);                          
                            setOriginSchools([...optionalSchools]); 

                            setSchools(filterSortSchools(matchedSchools, gradeFilters, wardFilters, offeringSorts, inBoundaryFilter));
                            
                            // set up boundary poly
                            let boundaryPoly = getBoundaryPolygons(llLocationObj, ["ES","MS","HS"])
                            boundaryPoly.then(data => {
                                setBoundaryPolys(data);
                            })
                        }
                        setLoadingData(false);
                    }).catch(error => {
                        console.error("Error in searching schools", error);
                        clearText();
                    });
                } else {
                    // need to set back to all if directly from URL params and invalid address 
                    clearText();
                }
            }).catch( error => {
                console.error("Error in searching address", error);
                clearText();
            });
        }
    }

    function getSuggestionElement(suggestion, index) {
        // get the suggestion elements and create list items in autocomplete
        const isSelected = selectedIndex !== undefined && suggestion === suggestionArr[selectedIndex];
        return (
            <li key={ suggestion.replace(" ", "_") + "_" + index } className={'cursor-pointer group block px-2 py-1 border-l-4 text-sm' + (isSelected ? ' auto--selected border-my-emerald bg-gray-100' : ' border-transparent')}
                onMouseDown={() => onSelect(suggestion)}>
                { suggestion }
            </li>
        );
    }

    function matchOptional(schools, optional)
    {
        let tempSchools = schools;
        // let rankValue = 1;

        tempSchools.map(school => {
            let proximity = false;
            let optionalType = [];

            if (optional.indexOf(school.properties.name) > -1) {
                proximity = true;
                optionalType.push()

            }
            school.properties.optional = proximity;
            return school;
        })
        return tempSchools;      
    }

    function matchInBoundary(schools, inBounds) {
        // match up in boundary schools and rerank
        let tempSchools = schools;
        let rankValue = 0;
        tempSchools.map(school => {
            let inBoundary = false;
            let inBoundaryType = [];
            // console.log('school.properties.name', school.properties.name);
            // console.log('in boundary middle school', inBounds.inBoundaryMiddleSchool);

            // if index of school name is found in boundary service
            if (inBounds.inBoundaryElementarySchool.indexOf(school.properties.name) > -1) {
                inBoundary = true;
                inBoundaryType.push("ES");
            }
            // added replace() to take care of bad hyphen in school name text for one particular school
            if((inBounds.inBoundaryMiddleSchool).replace("6–8", "6-8").indexOf(school.properties.name) > -1) {
                inBoundary = true;
                inBoundaryType.push("MS");
            }
            if(inBounds.inBoundaryHighSchool.indexOf(school.properties.name) > -1) {
                inBoundary = true;
                inBoundaryType.push("HS");
            }
            if (!inBoundary) {
                school.rank = rankValue;
                rankValue += 1;
            } else {
                school.rank = inBoundaryType.join(",");
            }
            school.properties.inBoundary = inBoundary;
            return school;
        })
        return tempSchools;
    }

    function findMatch(searchTerm) {
        // find matches for input string search term
        if (searchTerm.length === 0) {
            return [];
        } else {
            getAddress(searchTerm, false).then(
                response => {
                if (Array.isArray(response)) {
                    let tempArr = response;
                    setShowSuggestions(true);
                    setSuggestionArr(tempArr.slice(0, 5));
                } else {
                    return [];
                }

            },
            error => {
                console.log(error);
                return [];
            });
        }
    }

    function onSelect(selection){
        // on select item in drop down list - hide the suggestion list and run the search
        setSelectedAddress(selection);
        search(selection);
     }

    return (
        <div className='w-full font-sans'>
            <div className='relative ml-1 mr-1 mb-1'>
                <div className='w-5 text-slate-500 top-0 bottom-0 absolute left-2 h-full flex items-center cursor-pointer'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <input id="mar-search"
                    autoComplete="off"
                    className='appearance-none border text-sm w-full text-gray-700 py-1 px-1 pl-8 pr-6 leading-tight focus:outline-none focus:shadow-outline h-8 text-ellipsis overflow-hidden whitespace-nowrap'
                    type='text' aria-label='Search Address'
                    value={ value }
                    placeholder={t('filter.address.placeholder')}
                    onInput={ handleChange }
                    onKeyDown={e => onKeyDown(e)}
                    onBlur={ () => setShowSuggestions(false) } />
                <div className='w-5 text-slate-500 top-0 bottom-0 absolute right-0 pr-2 h-full flex items-center cursor-pointer' onClick={() => clearText()}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
                <ul role='listbox'
                    hidden={ !showSuggestions }
                    className='absolute shadow-lg z-[1500] bg-white overflow-hidden peer-checked:flex flex-col w-full mt-0 border border-gray-300'>
                    { suggestionArr.map((suggestion, index) => getSuggestionElement(suggestion, index)) }
                </ul>
            </div>
        </div>
    );
}

export default MARInput;
